import type { HTMLAttributes, KeyboardEvent } from 'react';
import { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../../basic/Icon';
import styles from './LabelMenu.module.css';

export interface LabelMenuOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface LabelMenuProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /** Opciones del menú */
  options: LabelMenuOption[];
  /** Valor seleccionado actualmente */
  value?: string;
  /** Callback cuando se selecciona una opción */
  onChange?: (value: string) => void;
  /** Si el menú está abierto */
  isOpen: boolean;
  /** Callback para cerrar el menú */
  onClose: () => void;
  /** Elemento trigger (botón) para posicionar el menú */
  triggerRef: React.RefObject<HTMLElement | null>;
  /** Callback para editar (botón Editar) */
  onEdit?: () => void;
  /** Callback para crear (botón Crear) */
  onCreate?: () => void;
  /** Si el botón crear está deshabilitado */
  createDisabled?: boolean;
  /** Tooltip para el botón crear cuando está deshabilitado */
  createTooltip?: string;
  /** Título del menú */
  title?: string;
  /** Container para el portal. Use null para render inline */
  portalContainer?: Element | null;
}

export function LabelMenu({
  options,
  value,
  onChange,
  isOpen,
  onClose,
  triggerRef,
  onEdit,
  onCreate,
  createDisabled,
  createTooltip,
  title = 'Etiqueta',
  portalContainer,
  className,
  ...props
}: LabelMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  // Posicionar el menú relativo al trigger
  useEffect(() => {
    if (!isOpen || !triggerRef.current || !menuRef.current) return;

    const trigger = triggerRef.current;
    const menu = menuRef.current;
    const rect = trigger.getBoundingClientRect();
    const safeArea = 24; // Safe area mínimo del borde (24px)

    // Primero, permitir que el menú se ajuste a su contenido
    menu.style.width = 'auto';
    menu.style.minWidth = 'auto';
    menu.style.maxWidth = 'auto';

    // Obtener el ancho natural del menú después de renderizar
    const menuRect = menu.getBoundingClientRect();
    const menuWidth = menuRect.width;

    // Posicionar arriba del botón, con el borde derecho del menú alineado al borde derecho del botón
    let left = rect.right - menuWidth; // Alinear borde derecho del menú con borde derecho del botón
    let top = rect.top - menuRect.height - 8; // Posicionar arriba del botón

    // Si no cabe arriba, posicionar debajo
    if (top < safeArea) {
      top = rect.bottom + 8;
    }

    // Ajustar si se sale por la izquierda
    if (left < safeArea) {
      left = safeArea;
    }

    // Ajustar si se sale por la derecha
    if (left + menuWidth + safeArea > window.innerWidth) {
      left = window.innerWidth - menuWidth - safeArea;
      // Si aún se sale, mantener alineado al borde derecho del trigger
      if (left < safeArea) {
        left = Math.max(safeArea, rect.right - menuWidth);
      }
    }

    // Ajustar si se sale por abajo (cuando está debajo del botón)
    if (top + menuRect.height + safeArea > window.innerHeight) {
      // Intentar posicionar arriba si estaba debajo
      const topPosition = rect.top - menuRect.height - 8;
      if (topPosition >= safeArea) {
        top = topPosition;
      } else {
        // Si tampoco cabe arriba, ajustar altura máxima
        top = safeArea;
        menu.style.maxHeight = `${window.innerHeight - safeArea * 2}px`;
      }
    }

    // Asegurar que no se salga por arriba
    if (top < safeArea) {
      top = safeArea;
      menu.style.maxHeight = `${window.innerHeight - safeArea * 2}px`;
    }

    menu.style.top = `${top}px`;
    menu.style.left = `${left}px`;
  }, [isOpen, triggerRef]);

  // Cerrar al hacer click fuera
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        triggerRef.current &&
        !triggerRef.current.contains(target)
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen, onClose, triggerRef]);

  // Manejar tecla ESC
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Navegación con teclado
  const handleMenuKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const options = Array.from(
        menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="option"]:not([disabled])') || []
      );
      const currentIndex = options.findIndex((opt) => opt === document.activeElement);

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          if (currentIndex < options.length - 1) {
            options[currentIndex + 1]?.focus();
          } else {
            options[0]?.focus();
          }
          break;
        case 'ArrowUp':
          event.preventDefault();
          if (currentIndex > 0) {
            options[currentIndex - 1]?.focus();
          } else {
            options[options.length - 1]?.focus();
          }
          break;
        case 'Home':
          event.preventDefault();
          options[0]?.focus();
          break;
        case 'End':
          event.preventDefault();
          options[options.length - 1]?.focus();
          break;
        case 'Escape':
          event.preventDefault();
          onClose();
          break;
      }
    },
    [onClose]
  );

  const handleSelect = useCallback(
    (optionValue: string) => {
      if (onChange) {
        onChange(optionValue);
      }
      onClose();
    },
    [onChange, onClose]
  );

  if (!isOpen) return null;

  const menuClasses = [
    styles.menu,
    styles.menuOpen,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const menuContent = (
    <div
      ref={menuRef}
      className={menuClasses}
      role="listbox"
      onKeyDown={handleMenuKeyDown}
      aria-label={title}
      {...props}
    >
      {/* Header */}
      <div className={styles.header}>
        <h3 className={styles.title}>{title}</h3>
        <button
          type="button"
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Cerrar menú"
        >
          <Icon name="close" size="sm" />
        </button>
      </div>

      {/* Options */}
      <div className={styles.options}>
        {options.length === 0 ? (
          <p className={styles.noOptions}>Sin registros</p>
        ) : options.map((option) => {
          const isSelected = option.value === value;
          const optionClasses = [
            styles.option,
            isSelected ? styles.optionSelected : '',
            option.disabled ? styles.optionDisabled : '',
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <button
              key={option.value}
              type="button"
              role="option"
              className={optionClasses}
              onClick={() => !option.disabled && handleSelect(option.value)}
              disabled={option.disabled}
              aria-selected={isSelected}
            >
              {option.label}
              {isSelected && (
                <Icon name="check" size="sm" className={styles.checkIcon} />
              )}
            </button>
          );
        })}
      </div>

      {/* Footer Actions */}
      {(onEdit || onCreate) && (
        <div className={styles.footer}>
          { options.length > 0 && onEdit && (
            <button
              type="button"
              className={styles.actionButton}
              onClick={onEdit}
            >
              Editar
            </button>
          )}
          {onCreate && (
            <button
              type="button"
              className={`${styles.actionButton} ${styles.actionButtonCreate}`}
              onClick={onCreate}
              disabled={createDisabled}
              title={createTooltip}
            >
              Crear
            </button>
          )}
        </div>
      )}
    </div>
  );

  // Si portalContainer es null, render inline (útil para Storybook)
  if (portalContainer === null) {
    return menuContent;
  }

  // Render en portal
  return createPortal(menuContent, portalContainer ?? document.body);
}

LabelMenu.displayName = 'LabelMenu';
